import React, { useState, useRef, useEffect } from 'react'
import { PlayIcon, PauseIcon, ChevronLeftIcon, Loader2Icon, InfoIcon  } from 'lucide-react'
import { useMutation } from '@tanstack/react-query'
import phoenixService from '@/services/phoenixSocket'

import { useVoiceRequest, VoiceRequest } from '@/services/api_client'

// Define Avatar interface based on your data structure
interface Avatar {
  id: string;
  name: string;
  images: string[];
  voiceSample: string;
  voiceId?: string; // Optional in case the voice_id is stored differently
}

interface ScriptWriterProps {
  avatar: Avatar
  onBack: () => void
  onSubmit: (audio_url: string, duration: number) => void
  setAudioUrl: (url: string | null) => void
  selectedImageIndex?: number
  setDuration: (duration: number | null) => void
  script: string
  setScript: (script: string) => void
  audioUrl: string
  duration: number | null
  user?: {
    id: string;
    name: string;
    email: string;
  }
  userCredits?: number | null
}

interface Request {
  id: string;
  status: string;
  text: string;
  voice_id: string;
  audio_url: string | null;
  duration?: number;
}

export const ScriptWriter: React.FC<ScriptWriterProps> = ({
  avatar,
  onBack,
  onSubmit,
  setAudioUrl,
  audioUrl,
  duration,
  selectedImageIndex = 0,
  setDuration,
  script,
  setScript,
  user,
  userCredits,
}) => {
  const [isPlaying, setIsPlaying] = useState(false)
  const [previewVoice, setPreviewVoice] = useState<Request | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const audioRef = useRef<HTMLAudioElement>(null)

  console.log("user", user)


  const { 
      createVoiceRequest, 
      loading: isLoadingVoicePreview, 
      response: voicePreviewResponse, 
      completed: voicePreviewCompleted,
      error: voicePreviewError
   } = useVoiceRequest();

  const maxLength = 200;

  // Store the last script value to detect changes
  const [lastScriptValue, setLastScriptValue] = useState('');
  
  // Handle script changes
  const handleScriptChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newScript = e.target.value;
    setScript(newScript);
    
    setPreviewVoice(null);
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  useEffect(() => {
    if (voicePreviewResponse) {
        setPreviewVoice(voicePreviewResponse.data);
    }
  }, [voicePreviewResponse]);

  useEffect(() => {

    if (!audioRef.current) {
      const audio = new Audio();
      audioRef.current = audio;
    }

    const audio = audioRef.current;

    const handlePlay = () => {
      setIsPlaying(true);
    };
    
    const handlePause = () => {
      setIsPlaying(false);
    };
    
    const handleEnded = () => {
      setIsPlaying(false);
    };

    // Attach event listeners
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('ended', handleEnded);
    };

  }, [previewVoice]);
  
  // Determine which icon to show
  const getPreviewIcon = () => {
    if (isLoadingVoicePreview) {
      return <Loader2Icon size={18} className="animate-spin" />;
    }
    if (isPlaying) {
      return <PauseIcon size={18} />;
    }
    return <PlayIcon size={18} />;
  };

  const togglePlayAudio = () => {
    if (!script.trim()) {
      return;
    }

    if (previewVoice?.status === 'completed' && previewVoice?.audio_url && audioRef.current) {
      // Check if the current preview text matches the current script
      if (previewVoice.text !== script.trim()) {
        // Text changed, we need a new preview
        return false; // Return false to indicate we need a new preview
      }
      
      // Toggle play/pause for existing audio
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        // Set src just to be sure it's correct
        audioRef.current.src = previewVoice.audio_url;
        audioRef.current.play().catch(err => {
          console.warn("Playback failed:", err);
        });
      }

      setIsPlaying(!isPlaying);
      return true; // Successfully toggled existing audio
    }
    
    return false; // No valid audio to toggle
  };

  const handlePreviewSubmit = async () => {
    if (!script.trim()) return;
    
    // Try to toggle existing audio if text matches
    if (script.trim() === previewVoice?.text && togglePlayAudio()) {
      return; // Audio was toggled successfully
    }
    
    try {
      const request: VoiceRequest = {
        voice_id: avatar.voiceId,
        text: script.trim(),
      };

      const result = await createVoiceRequest(request);

      // Store the preview
      setPreviewVoice({
        id: result.data.id,
        status: result.data.status,
        text: script.trim(),
        audio_url: result.data.audio_url
      });

      setAudioUrl(result.data.audio_url);
      setDuration(result.data.duration);
      
      // Play the audio automatically if available
      if (result.data.audio_url && audioRef.current) {
        audioRef.current.src = result.data.audio_url;
        // Add a slight delay to ensure audio is loaded
        setTimeout(() => {
          if (audioRef.current) {
            audioRef.current.play().catch(err => {
              console.warn("Auto-play failed:", err);
            });
          }
        }, 100);
      }
    } catch (err) {
      console.error('Failed to generate preview:', err);
    }
  };

  // Determine the audio source to use
  const audioSource = previewVoice?.status === 'completed' && previewVoice?.audio_url 
    ? previewVoice.audio_url 
    : null; // Don't use the default voice sample

  const getCounterColor = () => {
    if (script.length > maxLength * 0.9) return 'text-red-500';
    if (script.length > maxLength * 0.8) return 'text-orange-500';
    return 'text-gray-500';
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <div className="p-4 border-b border-gray-100 flex items-center gap-3">
        <button
          onClick={onBack}
          className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          aria-label="Go back"
        >
          <ChevronLeftIcon size={20} />
        </button>
        <h3 className="text-lg font-medium text-gray-800">
          Write a script for {avatar.name}
        </h3>
      </div>
      <div className="md:grid md:grid-cols-3 md:gap-6">
        <div className="p-4 md:p-6 md:border-r md:border-gray-100">
          <div className="max-w-[200px] md:max-w-none mx-auto">
            <div className="aspect-[16/9] rounded-lg overflow-hidden relative">
              <div className="absolute inset-0 bg-gray-900">
                <img
                  src={avatar.images[selectedImageIndex]}
                  alt={avatar.name}
                  className="w-full h-full object-contain object-center"
                />
              </div>
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: `url(${avatar.images[selectedImageIndex]})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  filter: 'blur(10px)',
                  opacity: 0.5,
                  transform: 'scale(1.1)',
                }}
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <img
                  src={avatar.images[selectedImageIndex]}
                  alt={avatar.name}
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
            <div className="mt-4">
              {voicePreviewError && (
                <div className="mt-2 text-sm text-red-500">
                  Failed to generate voice preview. Please try again.
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 md:p-6 md:col-span-2">
          <div className="max-w-2xl">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Script
            </label>
            <textarea
              value={script}
              onChange={handleScriptChange}
              maxLength={user?.customer_type === "free_user" ? maxLength : 99999999}
              rows={6}
              placeholder="Write your script here..."
              className="w-full h-36 p-3 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 resize-none"
            />
            <div className={`mt-2 text-sm text-gray-500 ${getCounterColor()}`}>
                {user?.customer_type === "free_user" && (
                  <div className={`mt-2 text-sm text-gray-500 ${getCounterColor()}`}>
                    {script.length} / {maxLength} characters
                  </div>
                )}
            </div>
          </div>
        </div>
      </div>
      <div className="p-4 bg-gray-50 border-t border-gray-100 flex justify-between">
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-700 cursor-pointer"
          disabled={isSubmitting}
        >
          Back
        </button>

        <div className="flex items-baseline">
          <div className="flex items-center gap-3 justify-between bg-gray-50 p-3 rounded-lg">
            <span className="text-sm text-gray-600 mr-4">
                {isLoadingVoicePreview && <p>Processing...</p>}
                {voicePreviewCompleted && <p>Voice generation completed!</p>}
            </span>
            <button
              className="px-4 py-1.5 rounded-lg bg-white ring-1 ring-purple-500 text-purple-600 font-medium hover:bg-purple-100 flex items-center justify-center gap-1.5 cursor-pointer"
              onClick={handlePreviewSubmit}
              aria-label={isLoadingVoicePreview ? 'Processing' : 'Preview'}
            >
              {getPreviewIcon()}
              Preview Audio
            </button> 
            {previewVoice && !isLoadingVoicePreview && <button
              className={`px-4 py-2 rounded-md ${
                !script.trim() || isSubmitting
                  ? 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  : 'bg-purple-600 text-white hover:bg-purple-700 cursor-pointer'
              }`}
              onClick={onSubmit}
              disabled={!script.trim() || isSubmitting}
            >
              {isSubmitting ? (
                <span className="flex items-center">
                  <LoaderIcon size={16} className="animate-spin mr-2" />
                  Generating...
                </span>
              ) : (
                "Generate Video"
              )}
            </button>
            }
          </div>
        </div>
      </div>
      {/* Only render audio element when we have a source */}
      {audioSource && (
        <audio
          ref={audioRef}
          src={audioSource}
          preload="auto"
        />
      )}
    </div>
  )
}
